import { useEffect, useState } from "react";

import { Loader2, Pencil, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { useReceptionModificationRequests } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import type { ReceptionModificationType } from "~/types/receptionModification";

interface RequestReceptionModificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receptionItemId: string;
  itemName: string;
  unit: string;
  currentQuantity: number;
  currentUnitCost: number;
  currentExpiryDate: string | null;
  initialMode?: ReceptionModificationType;
}

export function RequestReceptionModificationDialog({
  open,
  onOpenChange,
  receptionItemId,
  itemName,
  unit,
  currentQuantity,
  currentUnitCost,
  currentExpiryDate,
  initialMode = "edit",
}: RequestReceptionModificationDialogProps) {
  const { toast } = useToast();
  const { request, isRequesting } = useReceptionModificationRequests();

  const [mode, setMode] = useState<ReceptionModificationType>(initialMode);
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [expiry, setExpiry] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setQuantity(String(currentQuantity));
      setUnitCost(String(currentUnitCost ?? ""));
      setExpiry(currentExpiryDate ?? "");
      setReason("");
    }
  }, [open, initialMode, currentQuantity, currentUnitCost, currentExpiryDate]);

  const handleSubmit = async () => {
    if (reason.trim() === "") {
      toast({
        title: "Motivo requerido",
        description: "Indica el motivo de la modificación.",
        variant: "destructive",
      });
      return;
    }

    if (mode === "edit") {
      const qty = Number(quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        toast({
          title: "Cantidad inválida",
          description: "La cantidad debe ser mayor a 0. Para dejarla en 0 usa eliminar.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      await request({
        receptionItemId,
        requestType: mode,
        reason: reason.trim(),
        newQuantity: mode === "edit" ? Number(quantity) : null,
        newUnitCost: mode === "edit" && unitCost !== "" ? Number(unitCost) : null,
        newExpiryDate: mode === "edit" && expiry !== "" ? expiry : null,
      });
      onOpenChange(false);
    } catch {
      // toast handled in the hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitar modificación de recepción</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{itemName}</span> — la modificación
            queda pendiente de autorización y no afecta el stock hasta ser
            aprobada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={mode === "edit" ? "default" : "outline"}
              className="h-9"
              onClick={() => setMode("edit")}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
            <Button
              type="button"
              variant={mode === "delete" ? "destructive" : "outline"}
              className="h-9"
              onClick={() => setMode("delete")}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar
            </Button>
          </div>

          {mode === "edit" ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="rmod-qty">Cantidad ({unit})</Label>
                <Input
                  id="rmod-qty"
                  type="number"
                  className="h-9"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Actual: {currentQuantity.toLocaleString("es-CL")} {unit}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rmod-cost">Costo unitario (opcional)</Label>
                <Input
                  id="rmod-cost"
                  type="number"
                  className="h-9"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rmod-expiry">Vencimiento (opcional)</Label>
                <Input
                  id="rmod-expiry"
                  type="date"
                  className="h-9"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Se solicitará eliminar esta línea de recepción (
              {currentQuantity.toLocaleString("es-CL")} {unit}). Al aprobarse se
              revertirá el stock ingresado y la línea de la OC volverá a quedar
              pendiente.
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="rmod-reason">Motivo</Label>
            <Textarea
              id="rmod-reason"
              placeholder="Explica por qué se solicita esta modificación…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRequesting}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isRequesting}>
            {isRequesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar solicitud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
